import React, { useRef, useState } from "react";
import { 
	RCCBadge, 
	RCCButton, 
	RCCGrid, 
	RCCTooltip,  
	RCCDropDownItem, 
	RCCIcon,
	RCCInput
} from "react-common-components";
import { IForwardPagination } from "react-common-components/dist/components/Pagination/interface";
import { useForm } from "react-hook-form";
import { useTranslation } from "src/context/Translations/context";
import Pagination from "src/components/Pagination";
import FiltersContainer from "src/components/FiltersContainer";
import PageTitle from "src/components/PageTitle";
import OptionsColumn from "src/components/DefaultColumnsList/Options";
import Select from "src/components/Select";
import formatDate from "src/utils/formatDate";
import { IRefModalViewNotification } from "src/components/ModalViewNotification/interface";
import ModalViewNotification from "src/components/ModalViewNotification";
import AppIcons from "src/components/AppIcons";
import DateTimePicker from "src/components/DateTimePicker";
import { filterNotifications, IRefNotificationsModals, NotificationsDisplayTo, NotificationsType } from "./interface";
import { OverflowSpan } from "./styles";
import ModalNotifications, { getMessageValue } from "./modals";


const Notifications: React.FC = () => { 
	const { translate, currentLanguage } = useTranslation();
	const refModals = useRef<IRefNotificationsModals>(null);
	const refViewNotificationModal = useRef<IRefModalViewNotification>(null);
	
	// Filters
	const [showFilters, setShowFilters] = useState(false);
	const [filters, setFilters] = useState<filterNotifications>({
		filter: "",
		displayTo: "",
		date: formatDate(new Date())
	});
	const { handleSubmit : handleSubmitFilters, control:controlFilters, register : registerFilters, reset: resetFilters, watch: watchFilters } = useForm();
	const submitFilters = (data: any) => {
		const newState = {} as filterNotifications;
		newState.filter = data.filter;
		newState.displayTo =  data.displayTo.value;
		newState.date = data.date ? formatDate(data.date) : "";
		setFilters(newState);	

		paginationRef.current?.reload();
	};

	// List & Pagination
	const paginationRef = useRef<IForwardPagination>(null);
	const colOptions = (row: any) => (
		<OptionsColumn>
			<RCCDropDownItem
				selfEnclose
				onClick={() => { refViewNotificationModal.current?.openModalNotification(row.efn001Tipo, row.efn001Msgsimples, row.efn001Msgcompleta);  }}
			>
				<RCCIcon name="eye"
				/>
				{translate("Visualizar")}
			</RCCDropDownItem>
			<RCCDropDownItem
				selfEnclose
				onClick={() => { refModals.current?.actionButtonChange(row);  }}
			>
				<RCCIcon name="pencil" 
				/>
				{translate("Alterar")}
			</RCCDropDownItem>
			<RCCDropDownItem 
				selfEnclose
				onClick={() => { refModals.current?.actionButtonDelete(row); }}
			>
				<RCCIcon name="trash" 
				/>
				{translate("Excluir")}
			</RCCDropDownItem>
		</OptionsColumn>
	);

	const colType = (row: any) => {

		let color: "blue" | "red" | "green" | "yellow" | "transparent";
		let text = "";

		switch(row.efn001Tipo){
		case NotificationsType.SUCCESS:
			text = translate("Sucesso");
			color = "green";
			break;
		case NotificationsType.INFORMATION:
			text = translate("Informação");
			color = "blue";
			break;
		case NotificationsType.WARNING:
			text = translate("Aviso");
			color = "yellow";
			break;
		case NotificationsType.ERROR:
			text = translate("Erro");
			color = "red";
			break;
		default: 
			color = "transparent";
			break;
		}

		return (
			<RCCBadge color={color}
				styleInline="font-size: 0.83rem;"
			>
				{text}
			</RCCBadge>
		);
	};

	const colDisplayTo = (row: any) => {

		let color: "black" | "grey" | "orange" | "transparent";
		let text = "";

		switch(row.efn001Mostrar){
		case NotificationsDisplayTo.ALL_USERS:
			text = translate("Todos Usuários");
			color = "grey";
			break;
		case NotificationsDisplayTo.ADMINISTRATORS:
			text = translate("Administradores");
			color = "black";
			break;
		case NotificationsDisplayTo.NO_ADMINISTRATORS:
			text = translate("Não Administradores");
			color = "orange";
			break;
		default: 
			color = "transparent";
			break;
		}

		return (
			<RCCBadge color={color}
				styleInline="font-size: 0.83rem;"
			>
				{text}
			</RCCBadge>
		);
	};

	const colPeriod = (row : any) => (
		<>
			<RCCGrid container item alignItems="center">
				<RCCIcon 
					name="calendar"
					size={16}
					color="var(--default-color-font)"
					style={{marginRight: "4px"}}
				/>
				{formatDate(row.efn001Dtinicial, currentLanguage)} - { formatDate(row.efn001Dtfinal, currentLanguage)}
			</RCCGrid>
		</>
	);

	const colSimpleMessage = (row: any) => (
		<>
			<OverflowSpan title={row.msg}>{row.efn001Id} - {getMessageValue(row.msg, currentLanguage)}</OverflowSpan>
		</>
		
	);

	return (
		<>
			<RCCGrid
				container
				item
				direction="column"
			>
				{/* Header -> Results + Buttons */}
				<PageTitle
					title={translate("Notificações")}
					icon={() => <AppIcons name="configs" />}
				>
					<RCCTooltip
						text={translate("Nova notificação")}
						color="purple"
					>
						<RCCButton
							icon={{name: "plus", color: "#FFF", size: 18}}
							color="purple"
							layout="isRounded"
							onClick={() => { refModals.current?.actionButtonAdd(); }}
							size="md"
						/>
					</RCCTooltip>
					<RCCTooltip
						text={translate("Filtros")}
						color={showFilters || 
                            filters.filter || 
                            filters.displayTo ||
                            filters.date ? "blue" : "grey"}
					>
						<RCCButton
							icon={{name: "filter", color: "#FFF", size: 16}}
							color={showFilters || 
                                filters.filter || 
                                filters.displayTo ||
                                filters.date  ? "blue" : "grey"}
							layout="isRounded"
							size="md"
							onClick={() => setShowFilters((prevState) => !prevState)}
						/>
					</RCCTooltip>
				</PageTitle>

				{/* Filters */}
				{showFilters && (
					<FiltersContainer
						onSubmitFilters={handleSubmitFilters(submitFilters)}
						resetFilters={() => resetFilters({ 
							filter: "", 
							displayTo: {value: "", label: translate("Todos")},
							date: null
						})}
					>
						<RCCGrid
							container
							item
						>
							<RCCGrid
								container
								item
								direction="row"
								alignItems="center"
								spacing={1}
								xs={10}
							>
								<RCCGrid
									container 
									item
									xs={12}
									sm={6}
									md={4}
									lg={4}
								>
									<DateTimePicker
										control={controlFilters}
										label={translate("Data")}
										name = "date"
										defaultValue={new Date()}
										type="datePicker"
									/>
								</RCCGrid>
								<RCCGrid
									container 
									item
									xs={12}
									sm={4}
									md={3}
									lg={3}
								>
									<Select 
										control={controlFilters}
										name="displayTo" 
										options={[
											{value: "", label: translate("Todos")}, 
											{value: String(NotificationsDisplayTo.ALL_USERS), label: translate("Todos Usuários")}, 
											{value: String(NotificationsDisplayTo.ADMINISTRATORS), label: translate("Administradores")},
											{value: String(NotificationsDisplayTo.NO_ADMINISTRATORS), label: translate("Não Administradores")}
										]} 
										label={translate("Exibir para")}
										defaultValue={{value: "", label: translate("Todos")}}
										placeholder={translate("Selecionar Opção")}
										submit="value and label"
									/>
								</RCCGrid>
								<RCCGrid
									container 
									item
									xs={12}
									sm={8}
									md={4}
									lg={4}
								>
									<RCCInput
										name="filter" label={translate("Pesquisar")}
										register={registerFilters}
										defaultValue={watchFilters("filter")}
									/>
								</RCCGrid>
							</RCCGrid>
						</RCCGrid>
					</FiltersContainer>
				)}

				{/* List */}
				<Pagination 
					ref={paginationRef}
					dataOrRoute="Notifications/List"
					filtersData={filters}    
					columns={[
	  					{
							idColumn: "efn001Msgsimples",
							nameColumn: translate("Título"),
							xs: 12,
							sm: 6,
							md: 6,
							cellRenderer: colSimpleMessage
	  					},
						{
							idColumn: "efn001Period",
							nameColumn: translate("Período"),
							xs: 12,
							sm: 2,
							md: 2,
							cellRenderer: colPeriod
	  					},
						{
							idColumn: "efn001Mostrar",
							nameColumn: translate("Exibir para"),
							xs: 12,
							sm: 2,
							md: 2,
							cellRenderer: colDisplayTo
	  					},
						{
							idColumn: "efn001Tipo",
							nameColumn: translate("Tipo"),
							xs: 12,
							sm: 1,
							md: 1,
							cellRenderer: colType
	  					},
						{
							idColumn: "options",
							nameColumn: " ",
							justify: "right",
							xs: 12,
							sm: 1,
							md: 1,
							cellRenderer: colOptions,
							disableSort: true
						}
					]}
				/>
			</RCCGrid>

			<ModalNotifications ref={refModals} paginationRef={paginationRef} />
			<ModalViewNotification ref={refViewNotificationModal}/>
		</>
	);
};

export default Notifications;
